import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { authApi } from '@/api/endpoints'
import { errorMessage } from '@/api/client'
import { Alert, PageHeader } from '@/components/common/Ui'
import { roleLabel, useAuth } from '@/store/auth'
import { dateTime } from '@/lib/format'

const BLANK = { current_password: '', new_password: '', confirm: '' }

export default function Account() {
  const { user } = useAuth()
  const [form, setForm] = useState(BLANK)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  const { data: access } = useQuery({ queryKey: ['my-permissions'], queryFn: authApi.permissions })

  const change = useMutation({
    mutationFn: ({ current_password, new_password }) =>
      authApi.changePassword(current_password, new_password),
    onSuccess: () => {
      setForm(BLANK)
      setError('')
      setDone(true)
    },
    onError: (e) => {
      setDone(false)
      setError(errorMessage(e))
    },
  })

  const submit = (event) => {
    event.preventDefault()
    if (form.new_password !== form.confirm) {
      setError('The two new passwords do not match')
      return
    }
    change.mutate(form)
  }

  return (
    <div className="account-page">
      <PageHeader title="Your account" subtitle="Sign-in details and what your role allows" />

      <div className="account-grid">
        <section className="panel">
          <div className="panel-title">Signed in as</div>
          <dl className="kv">
            <dt>Name</dt>
            <dd>{user?.full_name}</dd>
            <dt>Email</dt>
            <dd className="mono">{user?.email}</dd>
            <dt>Role</dt>
            <dd>
              <span className={`badge role-${user?.role}`}>
                {roleLabel(user)}
              </span>
            </dd>
            <dt>Last sign-in</dt>
            <dd>{user?.last_login_at ? dateTime(user.last_login_at) : '—'}</dd>
          </dl>

          {access && (
            <>
              <p className="hint">{access.description}</p>
              <details>
                <summary>Permissions on this account</summary>
                <ul className="perm-list">
                  {access.permissions.map((p) => (
                    <li key={p} className="mono">
                      {p}
                    </li>
                  ))}
                </ul>
              </details>
              <p className="hint">
                Need something that isn&apos;t on this list? An admin can change your role.
              </p>
            </>
          )}
        </section>

        <section className="panel">
          <div className="panel-title">Change your password</div>
          <form onSubmit={submit}>
            <label className="field">
              <span>Current password</span>
              <input
                type="password"
                value={form.current_password}
                onChange={(e) => setForm({ ...form, current_password: e.target.value })}
                autoComplete="current-password"
                required
              />
            </label>
            <label className="field">
              <span>New password</span>
              <input
                type="password"
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                placeholder="At least 8 characters, letters and numbers"
                autoComplete="new-password"
                required
              />
            </label>
            <label className="field">
              <span>Repeat new password</span>
              <input
                type="password"
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                autoComplete="new-password"
                required
              />
            </label>

            {error && <div className="error-note">⚠️ {error}</div>}
            <Alert
              tone="success"
              message={done ? 'Password changed successfully.' : ''}
              onClose={() => setDone(false)}
            />

            <button type="submit" className="btn btn-primary" disabled={change.isPending}>
              {change.isPending ? 'Saving…' : 'Change password'}
            </button>
          </form>
        </section>
      </div>
    </div>
  )
}
